import { SHARED_ACCESS_CONFIG } from './access.config';

const ACCESS_GRANTED_VALUE = 'granted';
const LOCAL_BYPASS_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);

const safeWindow = () => (typeof window === 'undefined' ? null : window);

const isLocalBypassEnabled = () => {
  const w = safeWindow();

  if (!w) {
    return false;
  }

  return LOCAL_BYPASS_HOSTS.has(w.location.hostname);
};

const readSharedAccessMarker = () => {
  const w = safeWindow();

  if (!w) {
    return null;
  }

  return w.localStorage.getItem(SHARED_ACCESS_CONFIG.storageKey);
};

const writeSharedAccessMarker = () => {
  const w = safeWindow();

  if (!w) {
    return;
  }

  w.localStorage.setItem(SHARED_ACCESS_CONFIG.storageKey, ACCESS_GRANTED_VALUE);
};

const clearSharedAccessMarker = () => {
  const w = safeWindow();

  if (!w) {
    return;
  }

  w.localStorage.removeItem(SHARED_ACCESS_CONFIG.storageKey);
};

const getErrorMessage = async (response, fallbackMessage) => {
  try {
    const payload = await response.json();
    const message = payload?.message;
    return typeof message === 'string' && message.trim().length > 0 ? message : fallbackMessage;
  } catch (_error) {
    return fallbackMessage;
  }
};

const getGrantFailureMessage = async (response) => {
  if (response.status === 404) {
    return 'Secure access API not found. Run local server with `npx vercel dev`.';
  }

  if (response.status === 401) {
    return 'Access code is invalid.';
  }

  if (response.status >= 500) {
    return getErrorMessage(response, 'Secure access server is unavailable or not configured.');
  }

  return getErrorMessage(response, 'Unable to validate access code right now.');
};

export const hasSharedAccess = () => {
  if (isLocalBypassEnabled()) {
    return true;
  }

  return readSharedAccessMarker() === ACCESS_GRANTED_VALUE;
};

export const refreshSharedAccessStatus = async () => {
  if (isLocalBypassEnabled()) {
    writeSharedAccessMarker();
    return true;
  }

  try {
    const response = await fetch(SHARED_ACCESS_CONFIG.endpoints.status, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store'
    });

    if (!response.ok) {
      clearSharedAccessMarker();
      return false;
    }

    const payload = await response.json();
    const granted = Boolean(payload?.granted);

    if (granted) {
      writeSharedAccessMarker();
      return true;
    }

    clearSharedAccessMarker();
    return false;
  } catch (_error) {
    clearSharedAccessMarker();
    return false;
  }
};

export const grantSharedAccess = async (candidateCode) => {
  if (isLocalBypassEnabled()) {
    writeSharedAccessMarker();
    return { granted: true };
  }

  const code = typeof candidateCode === 'string' ? candidateCode.trim() : '';

  if (!code) {
    return {
      granted: false,
      error: 'Access code is required.'
    };
  }

  try {
    const response = await fetch(SHARED_ACCESS_CONFIG.endpoints.verify, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ accessCode: code })
    });

    if (!response.ok) {
      clearSharedAccessMarker();
      return {
        granted: false,
        error: await getGrantFailureMessage(response)
      };
    }

    const payload = await response.json();

    if (!payload?.granted) {
      clearSharedAccessMarker();
      return {
        granted: false,
        error: 'Access code is invalid.'
      };
    }

    writeSharedAccessMarker();
    return { granted: true };
  } catch (_error) {
    clearSharedAccessMarker();
    return {
      granted: false,
      error: 'Unable to validate access code right now.'
    };
  }
};

export const revokeSharedAccess = async () => {
  if (isLocalBypassEnabled()) {
    clearSharedAccessMarker();
    return;
  }

  try {
    await fetch(SHARED_ACCESS_CONFIG.endpoints.logout, {
      method: 'POST',
      credentials: 'include'
    });
  } catch (_error) {
    // Best-effort clear: remove local marker even if request fails.
  }

  clearSharedAccessMarker();
};
